package co.maytes.standupbot.dashboard.components.widgets

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.design.StatusHue
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.CSSColorValue

enum class BadgeTone { Neutral, Accent, AccentStrong, Success, Error, Warning }

private data class BadgePaint(val bg: String, val fg: String, val ring: String)

private fun badgePaint(tone: BadgeTone): BadgePaint = when (tone) {
    BadgeTone.Neutral -> BadgePaint(
        bg = Palette.surfaceSubtle.cssRgb(),
        fg = Palette.textSecondary.cssRgb(),
        ring = Palette.border.cssRgb(),
    )
    BadgeTone.Accent -> BadgePaint(
        bg = Palette.accentSubtle.cssRgb(),
        fg = Palette.accentText.cssRgb(),
        ring = Palette.accentBorder.cssRgb(),
    )
    BadgeTone.AccentStrong -> BadgePaint(
        bg = Palette.accent.cssRgb(),
        fg = Palette.onAccent.cssRgb(),
        ring = Palette.accentHover.cssRgb(),
    )
    BadgeTone.Success -> statusPaint(StatusHue.Success)
    BadgeTone.Error -> statusPaint(StatusHue.Danger)
    BadgeTone.Warning -> statusPaint(StatusHue.Warning)
}

private fun statusPaint(hue: StatusHue): BadgePaint {
    val status = Palette.status(hue)
    return BadgePaint(
        bg = status.subtle.cssRgb(),
        fg = status.onSubtle.cssRgb(),
        ring = status.border.cssRgb(),
    )
}

@Composable
fun Badge(
    label: String,
    tone: BadgeTone = BadgeTone.Neutral,
    modifier: Modifier = Modifier,
    leadingDot: CSSColorValue? = null,
    leadingIcon: (@Composable () -> Unit)? = null,
) {
    val paint = badgePaint(tone)
    Box(
        modifier = Modifier
            .classNames("sb-badge-morph")
            .borderRadius(Radius.full)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", "6px")
                property("width", "fit-content")
                property("justify-self", "start")
                property("padding", "3px 9px")
                property("background-color", paint.bg)
                property("color", paint.fg)
                property("box-shadow", "inset 0 0 0 1px ${paint.ring}")
                property("font-feature-settings", "\"tnum\" 1")
                property("font-variant-numeric", "tabular-nums")
                property("white-space", "nowrap")
            }
            .then(modifier),
    ) {
        if (leadingDot != null) {
            Box(
                modifier = Modifier
                    .borderRadius(Radius.full)
                    .styleModifier {
                        property("width", "6px")
                        property("height", "6px")
                        property("background-color", leadingDot.cssRgb())
                        property("flex-shrink", "0")
                    },
            )
        }
        if (leadingIcon != null) {
            Box(
                modifier = Modifier.styleModifier {
                    property("display", "inline-flex")
                    property("align-items", "center")
                    property("flex-shrink", "0")
                    property("margin-left", "-1px")
                },
            ) {
                leadingIcon()
            }
        }
        SpanText(
            text = label,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(550),
        )
    }
}
